import React, { useState, useCallback, useEffect } from 'react';
import { FormControl, TextField, CircularProgress, Link, ListItem } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import AddClinicDialog from './AddClinicDialog';
import useClinicDropdownOptions from './useClinicDropDownOptions';

export default function ClinicDropdown({
  value,
  onChange,
  label,
  error,
  required,
  helperText,
  onOptionsLoaded = null,
  defaultName = null,
  openDialogOverride = null,
  openDialogOverrideTimestamp = null,
}) {
  const [inputValue, setInputValue] = useState('');
  const [dialogData, setDialogData] = useState({ open: false, name: '' });

  const toggleDialog = useCallback((open, name = '') => {
    setDialogData({ open, name });
  }, []);

  const onSaveClinicSuccess = useCallback(
    (clinic) => {
      onChange({ target: { value: clinic.id } });
    },
    [onChange]
  );

  const { options, selected, loading } = useClinicDropdownOptions({
    value,
    onOptionsLoaded,
  });

  useEffect(() => {
    if (defaultName && openDialogOverride) {
      toggleDialog(true, defaultName);
    }
  }, [defaultName, openDialogOverride, openDialogOverrideTimestamp, toggleDialog]);

  return (
    <>
      <FormControl fullWidth error={error}>
        <Autocomplete
          freeSolo
          loading={loading}
          options={options}
          value={selected}
          inputValue={inputValue}
          onInputChange={(_, v) => setInputValue(v)}
          getOptionLabel={(option) => {
            if (!option) return '';
            if (typeof option === 'string') return option;
            if (option.inputValue) return option.inputValue;
            return option.name || '';
          }}
          isOptionEqualToValue={(o, v) => Boolean(o?.id && v?.id && o.id === v.id)}
          filterOptions={(options, params) => {
            const input = (params.inputValue || '').trim().toLowerCase();

            const filtered = options.filter(
              (option) => option?.name?.toLowerCase().includes(input)
            );

            if (input && !options.some((option) => option.name?.toLowerCase() === input)) {
              filtered.push({
                inputValue: params.inputValue,
                name: `Add "${params.inputValue}"`,
              });
            }

            return filtered;
          }}
          onChange={(_, newValue) => {
            if (!newValue) return;

            if (typeof newValue === 'string') {
              toggleDialog(true, newValue);
              return;
            }

            if (newValue.inputValue) {
              toggleDialog(true, newValue.inputValue);
              return;
            }

            onChange({ target: { value: newValue.id } });
          }}
          renderOption={(props, option) => (
            <ListItem {...props} key={option.id || option.inputValue}>
              {option.inputValue ? <Link color="info.main">{option.name}</Link> : option.name}
            </ListItem>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              required={required}
              error={!!error}
              helperText={error?.message || helperText}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </FormControl>

      <AddClinicDialog
        {...dialogData}
        onClose={() => toggleDialog(false)}
        onSuccess={onSaveClinicSuccess}
      />
    </>
  );
}
